import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table } from "@/components/ui/table";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { isMoney, formatMoney } from "@/features/invoicing/money";
import { documentStatusPresentation, paymentStatusPresentation } from "@/features/invoicing/status";
import type { InvoiceSummary } from "@/features/invoicing/types";
import { INVOICES_LIST_COPY } from "./copy";

/**
 * The receivables rows.
 *
 * Every cell is a field `InvoiceSummary` — the contract's list projection —
 * actually carries. Two columns the design frame lists are deliberately absent
 * rather than filled with a marker on every row:
 *
 *   - **payer legal name.** `InvoiceSummary` is the projection with "no lines, no
 *     PII", so it carries `payerId` and nothing else about the payer. Rendering a
 *     name here would mean a second read per row or an invented label; the
 *     reference is shown instead, and the detail page (which does read `payer`)
 *     carries the name.
 *   - **pay-link state.** The row carries no pay-link field at all: the link id
 *     lives on the detail document and the link text is returned exactly once.
 *     A column that is unavailable on 100% of rows is noise, not honesty.
 *
 * `paidLate` sits beside the payment badge because it is a stored field, not a
 * derived one: an invoice can be `paid` and late at the same time, and the
 * collections view is exactly where that matters. Money is formatted from the
 * contract's base-unit string through the BigInt helpers in
 * `features/invoicing/money.ts`; a malformed amount renders the shared
 * unavailable marker instead of throwing or guessing.
 */

export function InvoiceAmount({ invoice }: { invoice: InvoiceSummary }) {
  const total = invoice.totals?.total;
  if (!isMoney(total)) return <UnavailableValue label="Amount not readable" />;
  return (
    <span className="font-semibold text-ink">
      {formatMoney(total)}{" "}
      <span className="font-normal text-muted">{total.asset.symbol || invoice.assetSymbol}</span>
    </span>
  );
}

function invoiceLabel(invoice: InvoiceSummary): string {
  return invoice.number ?? invoice.invoiceId;
}

export function InvoiceRow({
  invoice,
  canDuplicate,
  duplicatePending,
  onDuplicate,
}: {
  invoice: InvoiceSummary;
  canDuplicate: boolean;
  duplicatePending: boolean;
  onDuplicate: (invoiceId: string, label: string) => void;
}) {
  const copy = INVOICES_LIST_COPY.table;
  const document = documentStatusPresentation(invoice.documentStatus);
  const payment = paymentStatusPresentation(invoice.paymentStatus);
  const label = invoiceLabel(invoice);
  const detailHref = `/stablecoins/invoices/view?id=${encodeURIComponent(invoice.invoiceId)}`;

  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-3 py-2.5">
        {/* The row's link to the detail document. The id is a URL query
            parameter (the route is `/stablecoins/invoices/view`), so it is
            encoded and never carries payer PII. */}
        <Link
          to={detailHref}
          aria-label={copy.openAria(label)}
          className="font-semibold text-primary hover:underline"
        >
          {invoice.number ?? copy.emptyNumber}
        </Link>
      </td>
      <td className="px-3 py-2.5">
        <span className="font-mono text-xs text-muted">{invoice.payerId || copy.emptyNumber}</span>
      </td>
      <td className="px-3 py-2.5 text-sm">
        <InvoiceAmount invoice={invoice} />
      </td>
      <td className="px-3 py-2.5 text-sm text-muted">{invoice.dueDate ?? copy.emptyDate}</td>
      <td className="px-3 py-2.5">
        <Badge variant={document.tone}>{document.label}</Badge>
      </td>
      <td className="px-3 py-2.5">
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <Badge variant={payment.tone}>{payment.label}</Badge>
          {invoice.paidLate && <Badge variant="warning">{copy.paidLate}</Badge>}
        </span>
      </td>
      {canDuplicate && (
        <td className="px-3 py-2.5 text-right">
          <Button
            variant="secondary"
            size="sm"
            disabled={duplicatePending}
            aria-label={copy.duplicateAria(label)}
            onClick={() => onDuplicate(invoice.invoiceId, label)}
          >
            {duplicatePending ? copy.duplicatePending : copy.duplicate}
          </Button>
        </td>
      )}
    </tr>
  );
}

/** The header row, and the column count `LoadingTable` needs to keep its shape. */
export function InvoiceTableHead({ canDuplicate }: { canDuplicate: boolean }) {
  const columns = INVOICES_LIST_COPY.table.columns;
  const headers = [
    columns.number,
    columns.payer,
    columns.amount,
    columns.dueDate,
    columns.documentStatus,
    columns.paymentStatus,
  ];
  return (
    <thead>
      <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
        {headers.map((header) => (
          <th key={header} className="px-3 py-2.5">
            {header}
          </th>
        ))}
        {canDuplicate && <th className="px-3 py-2.5" aria-label="Row actions" />}
      </tr>
    </thead>
  );
}

export function InvoiceTable({
  invoices,
  canDuplicate,
  duplicatePendingId,
  onDuplicate,
}: {
  invoices: InvoiceSummary[];
  canDuplicate: boolean;
  duplicatePendingId: string | null;
  onDuplicate: (invoiceId: string, label: string) => void;
}) {
  return (
    <Table className="dense">
      <InvoiceTableHead canDuplicate={canDuplicate} />
      <tbody>
        {invoices.map((invoice) => (
          <InvoiceRow
            key={invoice.invoiceId}
            invoice={invoice}
            canDuplicate={canDuplicate}
            duplicatePending={duplicatePendingId === invoice.invoiceId}
            onDuplicate={onDuplicate}
          />
        ))}
      </tbody>
    </Table>
  );
}

/** Columns `LoadingTable` should reserve, matching `InvoiceTableHead`. */
export function invoiceTableColumnCount(canDuplicate: boolean): number {
  return canDuplicate ? 7 : 6;
}
